<script setup lang="ts">
import type { AuthMeResponse } from "@/definitions/types";
import { onBeforeUnmount, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { Toaster } from "vue-sonner";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import SessionNotice from "@/components/SessionNotice.vue";
import { useAuthStore } from "@/stores/auth";
import { getApi } from "@/utils/apis";
import {
  currentPathForRedirect,
  goLoginPage,
  isExpiredToken,
  LOGIN_REASON_EXPIRED,
} from "@/utils/commands";
import { logger } from "@/utils/logger";
import { setSentryUser, setSentryWorkspace } from "@/utils/sentry";
import { startSessionWatch } from "@/utils/session";

/**
 * 앱 루트
 * - 레이아웃은 라우트 트리(`router.ts`)에서 부모 컴포넌트로 지정한다.
 * - 여기서는 전역 토스트 · 전역 확인 다이얼로그 · 탭 세션 안내만 배치한다.
 */
const route = useRoute();
const router = useRouter();

// 다른 탭의 토큰 변경 감지 · 사용 중 미리 재발급(30차 S30-01 · S30-02). 화면의 `beforeunload` 처리기보다 먼저 걸려야 해서 마운트 전에 건다.
startSessionWatch();

// DEV 전용 `[ui-focus-lost]` 감시(93차 C93-10) — 가입 단계 전환(`v-if`)도 잡도록 레이아웃이 아니라 앱 루트에서 건다. 동적 import 라 prod 번들에는 없다.
let stopFocusLost: (() => void) | undefined;
if (import.meta.env.DEV) {
  void import("@/components/focusLost").then(({ watchFocusLost }) => {
    stopFocusLost = watchFocusLost(router);
  });
}
onBeforeUnmount(() => {
  stopFocusLost?.();
});

/**
 * 로그인 상태면 `GET api/v1/auth/me` 로 서버 기준 정보를 다시 받아온다.
 * - persist 된 pinia 상태가 서버와 어긋난 경우(이름 변경, 워크스페이스 합류/탈퇴)를 보정한다.
 * - 401 이면 apis.ts 응답 인터셉터가 세션을 정리하고 로그인 페이지로 보낸다(응답은 undefined).
 */
async function loadMe(): Promise<void> {
  try {
    const response = await getApi<AuthMeResponse>("api/v1/auth/me", {
      failureAlert: false,
      // 사용자가 유발한 요청이 아니므로 백엔드가 꺼져 있어도 토스트를 띄우지 않는다.
      silent: true,
    });
    // 401 처리 시 인터셉터가 undefined 를 돌려주므로 옵셔널 체이닝이 필요하다.
    if (response?.success && response.data) {
      useAuthStore().applyMe(response.data);
    }
  } catch (e: unknown) {
    logger.warn("Failed to load my info:", e);
  }
}

onMounted(async () => {
  // 탭 제목은 라우터(`router.ts` afterEach)가 화면마다 정한다 — 여기서 제품 이름으로 되돌리면 첫 화면 제목을 덮는다(77차 UXS77-03).
  // 초기 내비게이션이 끝나기 전에는 route 가 실제 경로를 가리키지 않는다.
  // 청크 로드 실패 등으로 내비게이션이 실패하면 reject 되는데, 그때도 아래 분기는 이어가야 한다.
  await router.isReady().catch(() => undefined);
  // 구조분해하지 않고 스토어를 그대로 쓴다 — 아래 재발급이 `tokens` 를 새 값으로 바꾸므로 구조분해한 옛 값으로 판단하면 방금 회전된
  // 토큰을 버린다(27차 U27-01).
  const auth = useAuthStore();
  // 로그인·회원가입(인증 메일 링크 포함)처럼 계정 없이 들어오는 화면은 그대로 둔다.
  const isPublic = route.matched.some((record) => record.meta.public === true);
  // 공개 화면(로그인 · 회원가입 · 직무 입력 · 입사서류 제출 링크)은 공용 세션을 지우지도 · 재발급하지도 · 읽지도 않는다(30차 A30-01).
  // 토큰은 같은 브라우저의 탭들이 함께 쓰므로, 여기서 지우면 액세스만 만료된(운영 5분) 세션에서 이 화면을 새 탭으로 열기만 해도 열린 탭이
  // 모두 "다른 탭에서 로그아웃했습니다" 로 막혔다. 로그인 화면은 로그인에 성공할 때, 회원가입은 가입을 마칠 때 새 세션으로 바꾼다.
  if (isPublic) return;

  if (auth.loggedIn && isExpiredToken(auth.tokens.accessToken)) {
    // 액세스 토큰(운영 5분)만 만료됐고 리프레시 토큰(30분)이 살아 있으면 세션을 지우지 않고 먼저 재발급한다 — 새로고침 · 새 탭 ·
    // 딥링크가 화면 요청(`getValidatedAccessToken`)과 같은 길을 쓴다. 재발급은 스토어의 단일 비행 promise 를 공유한다(27차 U27-01).
    if (!isExpiredToken(auth.tokens.refreshToken)) {
      try {
        await auth.reIssueAccessToken();
      } catch (e: unknown) {
        logger.warn("Token renewal on startup failed:", e);
      }
    }
    // 판단 시점에 저장소의 최신 토큰을 다시 읽는다(재발급 · 먼저 끝난 화면 요청이 회전시킨 토큰 포함).
    if (!auth.loggedIn || isExpiredToken(auth.tokens.accessToken)) {
      auth.clearAuth();
      // 저장된 세션이 만료된 채 열렸다 — 로그인 화면이 이유를 말하게 한다(83차 JR83-12)
      await goLoginPage(currentPathForRedirect(), LOGIN_REASON_EXPIRED);
      return;
    }
  }
  if (!auth.loggedIn) {
    await goLoginPage(currentPathForRedirect());
    return;
  }

  const { info, workspace } = auth;

  // persist 로 복원된 세션에도 Sentry 컨텍스트를 붙인다(`loadMe` 가 실패해도 유지).
  setSentryUser(info);
  setSentryWorkspace(workspace);

  await loadMe();
});
</script>

<template>
  <RouterView />
  <ConfirmDialog />
  <SessionNotice />
  <!-- 72차 UXJ-02: 우상단은 AI 도우미 팝오버(380×600) 자리라 위 가운데로, 최대 4장 · 펼침 없음. tone 별 길이는 `toaster.ts` 가 넘기고
       아래 duration 은 그것을 거치지 않은 직접 호출의 기본값이다. 겹침은 `--ui-z-notice`(모달 위) — 라이브러리 기본 999999999 를 그대로 두면
       같은 위 가운데의 세션 안내(`SessionNotice`, 한 단계 위)를 오류 토스트(10초)가 덮어 「다시 로그인」 을 가린다. -->
  <Toaster
    rich-colors
    position="top-center"
    :visible-toasts="4"
    :toast-options="{ duration: 4_000, closeButtonAriaLabel: '알림 닫기' }"
    container-aria-label="알림"
    :style="{ zIndex: 'var(--ui-z-notice)' }"
  />
</template>
